import { c as _c } from "react/compiler-runtime";
import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { ProjectLocationEditorMenuItem } from "@components/project-location-editor/project-location-editor-menu-item";
import { DeleteMenuItem } from "@components/project/delete-menu-item";
import { isSdbProjectWithPosition } from "@custom-types/project-type-guards";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { ArchiveIcon, DeleteIcon, HolobuilderIcon, InfoIcon, LocationPinIcon, ManagementIcon, SelectionIcon, ShareLinkIcon, UnarchiveIcon, ViewerIcon } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ChangeManagementButtonMobile } from "@pages/project-details/project-overview/change-management/change-management-button-mobile";
import { isAllowedToChangeManagement } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { generateProjectDetailsRoute } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { toggleItemSelection } from "@utils/array-utils";
import { hasUserValidFeatureProjectLevel } from "@utils/feature-control/project/project-feature-control";
import { RequiredFeatureProjectLevelName } from "@utils/feature-control/project/project-feature-control-types";
import { hasDescribeOnlyPermission, hasUserValidPermissionProjectLevel } from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { showArchiveProjectDialog } from "@utils/project-archive-utils";
import { getOpenInTargetLabel, getOpenProjectHref, getProjectArchivingState, isFeatureEnabled, isProjectDemo, isProjectDownloaded, trackGoToProjectLocation, trackOpenProject, trackOpenProjectDetails } from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ShareProjectOption } from "@pages/projects/share-project-option";
import { UtilsTypes } from "@stellar/utils";
/**
 * Custom hook to get the menu options for a project.
 * Provides actions for viewing details, opening in Sphere XG/HoloBuilder, access level, archiving, sharing and management
 */
export function useProjectMenuOptions(t0) {
  const $ = _c(82);
  const {
    contextType,
    goTo
  } = t0;
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const {
    trackEvent,
    trackAsyncEvent
  } = useTrackEvent();
  const {
    createDialog
  } = useSphereDialog();
  const projectLaunchSettings = useAppSelector(defaultProjectLaunchTargetSelector);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    projects: viewMode
  } = useAppSelector(dataViewModeSelector);
  const isMapView = viewMode === "map" && !isScreenSmOrSmaller;
  const {
    canEditAllCompanyProjects
  } = useHasUserValidRoleCompanyLevel();
  const shouldShowProjectSelection = isScreenSmOrSmaller || isMapView;
  let t1;
  if ($[0] !== contextType || $[1] !== trackAsyncEvent) {
    t1 = async function trackAndOpenProject(project, openTarget) {
      await trackOpenProject({
        trackAsyncEvent,
        openFrom: contextType,
        numberOfMembers: project.members.length,
        openTarget,
        ...project
      });
      window.open(getOpenProjectHref({
        projectId: project.id,
        openTarget
      }), "_self");
    };
    $[0] = contextType;
    $[1] = trackAsyncEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trackAndOpenProject = t1;
  let t2;
  if ($[3] !== createDialog || $[4] !== dispatch || $[5] !== trackEvent) {
    t2 = async function handleArchiveAction(project_0) {
      await showArchiveProjectDialog({
        project: project_0,
        createDialog,
        dispatch,
        trackEvent
      });
    };
    $[3] = createDialog;
    $[4] = dispatch;
    $[5] = trackEvent;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleArchiveAction = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function isProjectArchived(project_1) {
      return getProjectArchivingState(project_1.archivingState) === UtilsTypes.ProjectArchivingState.archived;
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isProjectArchived = t3;
  let t4;
  if ($[8] !== dispatch || $[9] !== selectedRowIds) {
    t4 = project_2 => {
      const newSelectedRowIds = toggleItemSelection(selectedRowIds, project_2.id);
      dispatch(setInitialFetchingItems(newSelectedRowIds));
    };
    $[8] = dispatch;
    $[9] = selectedRowIds;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== shouldShowProjectSelection) {
    t5 = () => shouldShowProjectSelection;
    $[11] = shouldShowProjectSelection;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== shouldShowProjectSelection || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      label: "Select project",
      Icon: SelectionIcon,
      onSelect: t4,
      shouldShow: t5,
      shouldShowDividerAfter: shouldShowProjectSelection
    };
    $[13] = shouldShowProjectSelection;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== trackEvent || $[18] !== viewMode) {
    t7 = project_3 => {
      trackOpenProjectDetails({
        projectId: project_3.id,
        trackEvent,
        props: {
          eventLocation: getProjectArchivingState(project_3.archivingState),
          viewMode
        }
      });
    };
    $[17] = trackEvent;
    $[18] = viewMode;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== companyId) {
    t8 = project_4 => generateProjectDetailsRoute(companyId, project_4.id);
    $[20] = companyId;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = {
      label: "View details",
      Icon: InfoIcon,
      onSelect: t7,
      getHrefUrl: t8
    };
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      "& svg": {
        color: "inherit"
      },
      "&:hover svg": {
        color: "primary.main"
      }
    };
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== goTo || $[27] !== trackEvent) {
    t11 = project_5 => {
      if (!project_5.position || !goTo) {
        return;
      }
      trackGoToProjectLocation({
        trackEvent,
        source: "project card menu"
      });
      goTo(project_5.position);
    };
    $[26] = goTo;
    $[27] = trackEvent;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== goTo || $[30] !== isMapView) {
    t12 = project_6 => isMapView && goTo !== undefined && isSdbProjectWithPosition(project_6);
    $[29] = goTo;
    $[30] = isMapView;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = {
      label: "Go to",
      Icon: LocationPinIcon,
      menuItemSx: t10,
      onSelect: t11,
      shouldShow: t12
    };
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== isMapView) {
    t14 = {
      label: "Edit location",
      Icon: _temp,
      component: _temp2,
      shouldShow: () => isMapView
    };
    $[35] = isMapView;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== isScreenSmOrSmaller || $[38] !== projectLaunchSettings.canOpenWebEditor) {
    t15 = getOpenInTargetLabel({
      target: ProjectLaunchTarget.sphereViewer,
      canOpenWebEditor: projectLaunchSettings.canOpenWebEditor,
      shouldOmitVerb: isScreenSmOrSmaller
    });
    $[37] = isScreenSmOrSmaller;
    $[38] = projectLaunchSettings.canOpenWebEditor;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== trackAndOpenProject) {
    t16 = async project_8 => {
      await trackAndOpenProject(project_8, ProjectLaunchTarget.sphereViewer);
    };
    $[40] = trackAndOpenProject;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t15 || $[43] !== t16) {
    t17 = {
      label: t15,
      Icon: ViewerIcon,
      onSelect: t16,
      getHrefUrl: _temp3,
      shouldHandleNavigation: true,
      shouldShow: _temp4
    };
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== isScreenSmOrSmaller || $[46] !== projectLaunchSettings.canOpenWebEditor) {
    t18 = getOpenInTargetLabel({
      target: ProjectLaunchTarget.webEditor,
      canOpenWebEditor: projectLaunchSettings.canOpenWebEditor,
      shouldOmitVerb: isScreenSmOrSmaller
    });
    $[45] = isScreenSmOrSmaller;
    $[46] = projectLaunchSettings.canOpenWebEditor;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== trackAndOpenProject) {
    t19 = async project_11 => {
      await trackAndOpenProject(project_11, ProjectLaunchTarget.webEditor);
    };
    $[48] = trackAndOpenProject;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== projectLaunchSettings.canOpenWebEditor) {
    t20 = project_13 => projectLaunchSettings.canOpenWebEditor && !hasDescribeOnlyPermission(project_13);
    $[50] = projectLaunchSettings.canOpenWebEditor;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t18 || $[53] !== t19 || $[54] !== t20) {
    t21 = {
      label: t18,
      Icon: HolobuilderIcon,
      onSelect: t19,
      getHrefUrl: _temp5,
      shouldHandleNavigation: true,
      shouldShow: t20,
      shouldShowDividerAfter: true
    };
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== canEditAllCompanyProjects) {
    t22 = {
      label: "Management",
      Icon: ManagementIcon,
      component: _temp6,
      shouldShow: project_15 => canEditAllCompanyProjects || isAllowedToChangeManagement(isProjectDemo(project_15), project_15) && !isProjectArchived(project_15)
    };
    $[56] = canEditAllCompanyProjects;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== canEditAllCompanyProjects) {
    t23 = project_16 => {
      if (isProjectArchived(project_16)) {
        return false;
      }
      const hasFeatureRole = hasUserValidFeatureProjectLevel({
        roleName: RequiredFeatureProjectLevelName.canArchiveProject,
        featuresAvailable: project_16.featuresAvailable ?? undefined
      });
      return canEditAllCompanyProjects || hasFeatureRole && isFeatureEnabled(project_16, "project-archive");
    };
    $[58] = canEditAllCompanyProjects;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== handleArchiveAction || $[61] !== t23) {
    t24 = {
      label: "Archive",
      Icon: ArchiveIcon,
      onSelect: handleArchiveAction,
      shouldShow: t23,
      shouldShowDividerAfter: true
    };
    $[60] = handleArchiveAction;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== canEditAllCompanyProjects) {
    t25 = project_17 => {
      if (!isProjectArchived(project_17) || isProjectDownloaded(project_17)) {
        return false;
      }
      const hasFeatureRole_0 = hasUserValidFeatureProjectLevel({
        roleName: RequiredFeatureProjectLevelName.canUnarchiveProject,
        featuresAvailable: project_17.featuresAvailable ?? undefined
      });
      return canEditAllCompanyProjects || hasFeatureRole_0 && isFeatureEnabled(project_17, "project-unarchive");
    };
    $[63] = canEditAllCompanyProjects;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== handleArchiveAction || $[66] !== t25) {
    t26 = {
      label: "Unarchive",
      Icon: UnarchiveIcon,
      onSelect: handleArchiveAction,
      shouldShow: t25,
      shouldShowDividerAfter: true
    };
    $[65] = handleArchiveAction;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      label: "Share",
      Icon: ShareLinkIcon,
      component: _temp7,
      shouldShow: _temp8
    };
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== canEditAllCompanyProjects) {
    t28 = {
      label: "Delete",
      Icon: DeleteIcon,
      component: _temp9,
      shouldShow: project_21 => {
        const hasPermissions = canEditAllCompanyProjects || hasUserValidPermissionProjectLevel({
          permissionName: RequiredPermissionProjectLevelName.canDeleteProject,
          selectedProject: project_21
        });
        return hasPermissions && isProjectArchived(project_21);
      }
    };
    $[69] = canEditAllCompanyProjects;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t13 || $[72] !== t14 || $[73] !== t17 || $[74] !== t21 || $[75] !== t22 || $[76] !== t24 || $[77] !== t26 || $[78] !== t28 || $[79] !== t6 || $[80] !== t9) {
    t29 = [t6, t9, t13, t14, t17, t21, t22, t24, t26, t27, t28];
    $[71] = t13;
    $[72] = t14;
    $[73] = t17;
    $[74] = t21;
    $[75] = t22;
    $[76] = t24;
    $[77] = t26;
    $[78] = t28;
    $[79] = t6;
    $[80] = t9;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  const menuOptions = t29;
  return menuOptions;
}
function _temp9(project_20) {
  return <DeleteMenuItem type="project" entity={project_20} />;
}
function _temp8(project_19) {
  return !hasDescribeOnlyPermission(project_19);
}
function _temp7(project_18, onMenuClose_0) {
  return <ShareProjectOption project={project_18} onClose={onMenuClose_0} />;
}
function _temp6(project_14, onMenuClose) {
  return <ChangeManagementButtonMobile project={project_14} onClose={onMenuClose} />;
}
function _temp5(project_12) {
  return getOpenProjectHref({
    projectId: project_12.id,
    openTarget: ProjectLaunchTarget.webEditor
  });
}
function _temp4(project_10) {
  return !hasDescribeOnlyPermission(project_10);
}
function _temp3(project_9) {
  return getOpenProjectHref({
    projectId: project_9.id,
    openTarget: ProjectLaunchTarget.sphereViewer
  });
}
function _temp2(project_7, closeMenu) {
  return <ProjectLocationEditorMenuItem project={project_7} closeMenu={closeMenu} />;
}
function _temp() {
  return <></>;
}